import { Fragment } from 'react/jsx-runtime'

import {
  ContextMenuGroup,
  ContextMenuItem,
  ContextMenuSeparator,
  ContextMenuShortcut,
} from '@/components/ui/context-menu'

import { formatForDisplay } from '@tanstack/react-hotkeys'

import type { CommandGroup } from '@/shared/commands/command.types'
import {
  executeCommand,
  getVisibleCommandGroups,
  isCommandEnabled,
} from '@/shared/commands/command.utils'
import { isEmptyArray } from '@/utils/primitives'

import { useGridSnap } from '../grid/grid.store'

import type { CellCommandContext } from './cell.commands'
import { cellCommands, useCellCommandContext } from './cell.commands'
import { CellContext } from './cell.context'

const cellContextMenuItems: CommandGroup<CellCommandContext>[] = [
  {
    commands: [cellCommands.edit, cellCommands.history],
  },
  {
    commands: [cellCommands.copy, cellCommands.paste],
  },
  {
    commands: [cellCommands.clear],
  },
]

export function CellContextMenu() {
  const { contextMenu } = useGridSnap()

  if (!contextMenu || contextMenu.type !== 'cell') return null

  const { cellKey } = contextMenu

  return (
    <CellContext.Provider value={cellKey}>
      <CellContextMenuItems />
    </CellContext.Provider>
  )
}

function CellContextMenuItems() {
  const context = useCellCommandContext()

  const visibleGroups = getVisibleCommandGroups(cellContextMenuItems, context)

  return visibleGroups.map((group, index) => (
    <Fragment key={index}>
      {index > 0 && <ContextMenuSeparator />}

      <ContextMenuGroup>
        {group.commands.map((command) => (
          <ContextMenuItem
            key={command.id}
            disabled={!isCommandEnabled(command, context)}
            onClick={() => executeCommand(command, context)}
          >
            {command.label}

            {!isEmptyArray(command.shortcuts) && (
              <ContextMenuShortcut>
                {formatForDisplay(command.shortcuts?.[0] ?? '', {
                  separatorToken: '',
                })}
              </ContextMenuShortcut>
            )}
          </ContextMenuItem>
        ))}
      </ContextMenuGroup>
    </Fragment>
  ))
}
